/**
 * In-flight + short TTL cache for localization language list / modules.
 * Prevents StrictMode remounts and I18n auth-sync from issuing parallel
 * GET /localization/* requests during boot.
 */

import {
  fetchEnabledLanguages,
  fetchLocalizationModule,
  type Language,
  type LocalizationContext,
  type LocalizationModulePayload,
} from "./localization";

const TTL_MS = 60_000;

let inflight: Promise<Language[]> | null = null;
let cached: { at: number; list: Language[] } | null = null;

const moduleInflight = new Map<string, Promise<LocalizationModulePayload>>();

const FALLBACK_EN: Language[] = [
  {
    id: "en",
    code: "en",
    name: "English",
    description: null,
    flagUrl: null,
    enabled: true,
    isDefault: true,
  },
];

export function peekCachedEnabledLanguages(): Language[] | null {
  if (!cached) return null;
  if (Date.now() - cached.at > TTL_MS) return null;
  return cached.list;
}

export async function getEnabledLanguagesCached(
  opts?: { force?: boolean },
): Promise<Language[]> {
  if (!opts?.force) {
    const hit = peekCachedEnabledLanguages();
    if (hit) return hit;
    if (inflight) return inflight;
  } else {
    cached = null;
  }

  if (inflight && !opts?.force) return inflight;

  const request = fetchEnabledLanguages()
    .then((list) => {
      cached = { at: Date.now(), list };
      return list;
    })
    .catch(() => {
      // Keep a usable fallback so boot/auth sync does not churn retries.
      if (!cached) cached = { at: Date.now(), list: FALLBACK_EN };
      return cached.list;
    })
    .finally(() => {
      if (inflight === request) inflight = null;
    });

  inflight = request;
  return request;
}

export function invalidateEnabledLanguagesCache(): void {
  cached = null;
  inflight = null;
}

export function fetchLocalizationModuleCached(
  context: LocalizationContext,
  lang: string,
  sinceVersion?: number,
): Promise<LocalizationModulePayload> {
  const key = `${lang}:${context}:${sinceVersion ?? ""}`;
  const existing = moduleInflight.get(key);
  if (existing) return existing;

  const request = fetchLocalizationModule(context, lang, sinceVersion).finally(
    () => {
      if (moduleInflight.get(key) === request) moduleInflight.delete(key);
    },
  );
  moduleInflight.set(key, request);
  return request;
}
